import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import { fr } from "yup-locales";
import { setLocale } from "yup";
import {
  cleanerError,
  normalizeDecimalInput,
  parseMoneyToCents,
  stripTrailingZeros,
} from "@/lib/utils";
import {
  useCreateIngredientMutation,
  useUpdateIngredientMutation,
} from "@/redux/services/inventory/ingredient.api";
import { Ingredient } from "@/redux/services/inventory/inventory.type";
import { toastApiError } from "@/lib/apiErrors";

setLocale(fr);

const DECIMAL_REGEX = /^\d+(\.\d{1,6})?$/;
const PRICE_REGEX = /^\d+(\.\d{1,2})?$/;

export interface IngredientFormData {
  name: string;
  baseUnit: string;
  lowStockThreshold?: string;
  currentStock?: string;
  costPerUnit?: string;
}

export const ingredientValidationSchema: yup.ObjectSchema<IngredientFormData> =
  yup.object().shape({
    name: yup.string().required().label("Nom"),
    baseUnit: yup.string().required().label("Unité"),
    lowStockThreshold: yup
      .string()
      .optional()
      .test(
        "is-decimal",
        "Le seuil doit être un nombre positif (max 6 décimales)",
        (value) =>
          !value?.trim() ||
          DECIMAL_REGEX.test(normalizeDecimalInput(value) ?? "")
      )
      .label("Seuil"),
    currentStock: yup
      .string()
      .optional()
      .test(
        "is-decimal",
        "Le stock initial doit être un nombre positif (max 6 décimales)",
        (value) =>
          !value?.trim() ||
          DECIMAL_REGEX.test(normalizeDecimalInput(value) ?? "")
      )
      .label("Stock initial"),
    costPerUnit: yup
      .string()
      .optional()
      .test(
        "is-price",
        "Le coût doit être un nombre positif (max 2 décimales)",
        (value) =>
          !value?.trim() || PRICE_REGEX.test(normalizeDecimalInput(value) ?? "")
      )
      .label("Coût par unité"),
  });

export const useCrudIngredient = (
  closeModal: () => void,
  restaurantId: string,
  item?: Ingredient
) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors,
    setValue,
    watch,
  } = useForm<IngredientFormData>({
    resolver: yupResolver(ingredientValidationSchema),
    defaultValues: {
      name: item?.name ?? "",
      baseUnit: item?.baseUnit ?? "",
      lowStockThreshold: item?.lowStockThreshold
        ? stripTrailingZeros(item.lowStockThreshold)
        : "",
      currentStock: "",
      costPerUnit: "",
    },
  });

  const [createIngredient, { isLoading: isCreating }] =
    useCreateIngredientMutation();
  const [updateIngredient, { isLoading: isUpdating }] =
    useUpdateIngredientMutation();
  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    reset({
      name: item?.name ?? "",
      baseUnit: item?.baseUnit ?? "",
      lowStockThreshold: item?.lowStockThreshold
        ? stripTrailingZeros(item.lowStockThreshold)
        : "",
      currentStock: "",
      costPerUnit: "",
    });
  }, [item, reset]);

  useEffect(() => {
    if (errors) {
      cleanerError(errors, clearErrors);
    }
  }, [errors, clearErrors]);

  const onSubmit = async (data: IngredientFormData) => {
    const basePayload = {
      name: data.name.trim(),
      baseUnit: data.baseUnit.trim(),
      lowStockThreshold:
        normalizeDecimalInput(data.lowStockThreshold ?? "") ?? "0",
    };

    const cost = data.costPerUnit?.trim();

    const res = item?.id
      ? await updateIngredient({
          restaurantId,
          ingredientUuid: item.id,
          data: basePayload,
        })
      : await createIngredient({
          restaurantId,
          data: {
            ...basePayload,
            currentStock: normalizeDecimalInput(data.currentStock ?? "") ?? "0",
            costPerUnit: parseMoneyToCents(cost) ?? 0,
          },
        });

    if ("data" in res) {
      toast.success(
        item?.id
          ? "Ingrédient modifié avec succès"
          : "Ingrédient créé avec succès"
      );
      reset();
      closeModal();
    } else if ("error" in res) {
      toastApiError(res.error);
    }
  };

  return {
    register,
    errors,
    setValue,
    watch,
    onSubmit: handleSubmit(onSubmit),
    isLoading,
  };
};
